import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { IAuthorizedDiscountPercentageComponentProps } from "./AuthorizedDiscountPercentageComponent.types";
import {
  DiscountListItem,
  DiscountTypes,
  InitialStateOfResponseMessage,
} from "../../Common/QuotationData";
import {
  stackTokensWithGap5,
} from "../../QuotationStyle";
import {
  Checkbox,
  ComboBox,
  CommandBar,
  IComboBox,
  IComboBoxOption,
  ICommandBarItemProps,
  Stack,
  TextField,
} from "@fluentui/react";
import { IResponseMessage, QuotationInfoType } from "../../Quotation.types";
import { QuotationContext } from "../../QuotationContext";
import {
  comboBoxStyles,
  disbaledComboBoxStyles,
  textFieldGrayStyles,
  textFieldStyles,
} from "../AuthorizedDiscountComponent/AuthorizedDiscountComponentStyle";
import { hasDuplicateKeys } from "../../../../Utils/DuplicateKeys";
import { DeviceType } from "../../../../common/enum/DeviceTypes";
import { dateDifferenceInDays } from "../../../../Utils/Date";
import { decimalValueRegex, wholeNumberRegex } from "../../../../Utils/Common";
import {
  IAuthorizedDiscountComponentRef,
  IExtendedDiscountItem,
} from "../AuthorizedDiscountComponent/AuthorizedDiscountComponent.types";
import { onErrorCapture } from "../AuthorizedDiscountComponent/AuthorizedDiscountComponent";

export const AuthorizedDiscountPercentageComponent = forwardRef<
  IAuthorizedDiscountComponentRef,
  IAuthorizedDiscountPercentageComponentProps
>((props, ref) => {
  const {
    discountData,
    discountReasons,
    discountList,
    setDiscountList,
    selectedPivot,
    dismissDiscountPanel,
    selectedProduct,
    selectedProductIndex,
    selection,
    setLoading,
    showAlert
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [totalPercentage, setTotalPercentage] = useState<number>(0);
  const [discountSuccessMessage, setDiscountSuccessMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [discountFailureMessage, setDiscountFailureMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [discountInfoMessage, setDiscountInfoMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  const productNetAmount = selectedProduct.msrp * selectedProduct.quantity;
  useImperativeHandle(ref, () => ({
    onClickApplyDiscount,
  }));
  useEffect(() => {
    const total: number = discountList.reduce(
      (sum: number, item: IExtendedDiscountItem) => {
        return sum + Number(item.authorizedDiscountPercentage);
      },
      0
    );
    setTotalPercentage(Number(total.toFixed(2)));
    const errorMessage = `Unable to apply the discount more than 100%`;
    if (Number(total) > Number(100)) {
      setDiscountFailureMessage({
        message: errorMessage,
        showMessage: true,
      });
    } else if (
      Number(total) <= Number(100) &&
      discountFailureMessage.showMessage &&
      discountFailureMessage.message === errorMessage
    ) {
      setDiscountFailureMessage({
        message: "",
        showMessage: false,
      });
    }
  }, [discountList]);
  const addItemInDiscountList = () => {
    if (discountData.length > 0) {
      setDiscountInfoMessage(InitialStateOfResponseMessage);
      const { contractSelected, expirationDate, minimumDiscount } =
        discountData[0];
      const dateDifference: number = dateDifferenceInDays(
        new Date(expirationDate),
        new Date()
      );
      setDiscountList([
        ...discountList,
        {
          ...DiscountListItem,
          authorizedDiscountReason: contractSelected.toString(),
          authorizedDiscountDateError: dateDifference > 0 ? true : false,
          authorizedDiscountReasonObj: {
            key: contractSelected,
            text: contractSelected,
          },
          expirationDate: expirationDate,
          minimumAuthorizedDiscountAmount: minimumDiscount
            ? Number(minimumDiscount)
            : 0,
        },
      ]);
    }
    else if (showAlert) {
      setDiscountInfoMessage({
        message: "There are no authorized discounts available at this time. Please navigate to the customer discount section to apply any applicable discounts.",
        showMessage: true,
      });
    }
  };
  useEffect(() => {
    if (discountList.length === 0) {
      addItemInDiscountList();
    }
  }, [discountReasons, showAlert]);
  const renderCommandBar = () => {
    const _items: ICommandBarItemProps[] = [
      {
        key: "add",
        text: "Add",
        iconProps: { iconName: "Add" },
        onClick: () => {
          addItemInDiscountList();
        },
      },
      {
        key: "Edit",
        text: "Edit",
        iconProps: { iconName: "Edit" },
        disabled:
          selectedProduct?.authDiscountAmounts?.length ===
            0
            ? true
            : false,
        onClick: () => {
          setIsEditing(true);
        },
      },
      {
        key: "Delete",
        text: "Delete",
        iconProps: { iconName: "Delete" },
        onClick: () => {
          const filteredItems = discountList.filter((item) => !item.isChecked);
          setDiscountList(filteredItems);
        },
      },
    ];
    return (
      <CommandBar
        items={_items}
        ariaLabel="Inbox actions"
        primaryGroupAriaLabel="Email actions"
        farItemsGroupAriaLabel="More actions"
      />
    );
  };
  const isValidMinimumDiscount = (
    value: number | string,
    minimumDiscount: number | string
  ) => {
    return Number(value) < Number(minimumDiscount);
  };
  const renderDiscountItem = (
    item: IExtendedDiscountItem,
    itemIndex: number
  ) => {
    return (
      <Stack tokens={stackTokensWithGap5}>
        <Stack horizontal horizontalAlign="space-between" key={itemIndex + 1}>
          <Checkbox
            checked={item.isChecked}
            required
            ariaLabel="checkbox"
            styles={{ root: { top: "35px" } }}
            onChange={(e, newValue) => {
              discountList.splice(itemIndex, 1, {
                ...item,
                isChecked: newValue ?? false,
              });
              setDiscountList([...discountList]);
            }}
          />
          <TextField
            styles={
              (item.isEditable && !isEditing) ||
                item.authorizedDiscountDateError
                ? textFieldGrayStyles
                : textFieldStyles
            }
            label="Discount Percent"
            placeholder="Enter Percentage"
            name="authorizedDiscount"
            type="numeric"
            min={0}
            errorMessage={
              item.authorizedDiscountError
                ? "Value should be greater than 0"
                : item.authorizedDiscountDecimalError
                  ? quotationInfo.customer?.country === "Japan"
                    ? `Only whole numbers allowed for Japan`
                    : `Only 2 digits allowed after decimal`
                  : item.minimumAuthorizedDiscountError
                    ? `Discount amount should be ${item.minimumAuthorizedDiscountAmount}% of MSRP or more`
                    : ""
            }
            pattern={(quotationInfo.customer?.country === "Japan" ? wholeNumberRegex : decimalValueRegex).toString()}
            value={item.authorizedDiscountPercentage.toString()}
            readOnly={
              (item.isEditable && !isEditing) ||
                item.authorizedDiscountDateError
                ? true
                : false
            }
            required
            onChange={(event, newValue) => {
              // Use different regex pattern based on country
              const isJapan = quotationInfo.customer?.country === "Japan";
              const regexToUse = isJapan ? wholeNumberRegex : decimalValueRegex;

              discountList.splice(itemIndex, 1, {
                ...item,
                authorizedDiscountDecimalError: newValue
                  ? !regexToUse.test(newValue)
                  : false,
                minimumAuthorizedDiscountError:
                  newValue &&
                    isValidMinimumDiscount(
                      newValue,
                      item.minimumAuthorizedDiscountAmount
                    )
                    ? true
                    : false,
                authorizedDiscountError:
                  newValue === "" || Number(newValue) === 0 ? true : false,
                authorizedDiscountPercentage:
                  newValue && regexToUse.test(newValue)
                    ? newValue
                    : newValue === ""
                      ? ""
                      : item.authorizedDiscountPercentage,
              });
              setDiscountList([...discountList]);
            }}
          />
          <ComboBox
            data-testid="discount-reason-select"
            styles={
              item.isEditable && !isEditing
                ? disbaledComboBoxStyles
                : comboBoxStyles
            }
            label="Discount Reason"
            errorMessage={
              item.authorizedDiscountDateError
                ? "Selected discount got expired"
                : ""
            }
            required
            disabled={item.isEditable && !isEditing ? true : false}
            autoComplete="on"
            options={discountReasons}
            useComboBoxAsMenuWidth
            dropdownWidth={10}
            selectedKey={
              item.authorizedDiscountReason
                ? item.authorizedDiscountReason
                : undefined
            }
            onChange={(
              event: React.FormEvent<IComboBox>,
              option?: IComboBoxOption | undefined,
              index?: number | undefined,
              value?: string | undefined
            ) =>
              onChangeDiscountReason(
                item,
                itemIndex,
                event,
                option,
                index,
                value
              )
            }
            allowFreeInput
          />
        </Stack>
      </Stack>
    );
  };
  const renderDiscountList = () => {
    return discountList?.map((item: IExtendedDiscountItem, index: number) => {
      return renderDiscountItem(item, index);
    });
  };
  const onChangeDiscountReason = (
    item: IExtendedDiscountItem,
    itemIndex: number,
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
    index?: number | undefined,
    value?: string | undefined
  ) => {
    if (option) {
      let findIndex = discountData.findIndex(
        (e) => e.contractSelected === option.key
      );
      
      // Handle Deal Registration Booster as a special case
      if (option.key === "Deal Registration Booster") {
        // Find the Deal Registration discount to use its expiration date
        findIndex = discountData.findIndex(
          (e) => e.contractSelected === "Deal Registration"
        );
      }
      
      const dateDifference: number = dateDifferenceInDays(
        new Date(discountData[findIndex].expirationDate),
        new Date()
      );
      discountList.splice(itemIndex, 1, {
        ...item,
        minimumAuthorizedDiscountAmount:
          findIndex > -1 ? Number(discountData[findIndex].minimumDiscount) : 0,
        expirationDate:
          findIndex > -1 ? discountData[findIndex].expirationDate : "",
        authorizedDiscountDecimalError: !((quotationInfo.customer?.country === "Japan") ? 
          wholeNumberRegex.test(item.authorizedDiscountPercentage.toString()) : 
          decimalValueRegex.test(item.authorizedDiscountPercentage.toString())
        ),
        minimumAuthorizedDiscountError:
          item.authorizedDiscountPercentage &&
            discountData[findIndex].minimumDiscount &&
            isValidMinimumDiscount(
              item.authorizedDiscountPercentage,
              discountData[findIndex].minimumDiscount
            )
            ? true
            : false,
        authorizedDiscountDateError: dateDifference > 0 ? true : false,
        authorizedDiscountReasonObj: option,
        authorizedDiscountReason: option.key.toString(),
      });
      setDiscountList([...discountList]);
    }
  };
  const validateDiscounts = () => {
    const errorMessage = `Unable to apply the discount more than 100%`;
    if (Number(totalPercentage) > Number(100)) {
      setDiscountFailureMessage({
        message: errorMessage,
        showMessage: true,
      });
      return false;
    }
    if (hasDuplicateKeys(discountList, "authorizedDiscountReason")) {
      setDiscountFailureMessage({
        showMessage: true,
        message: "Unable to apply same discount multiple times.",
      });
      setTimeout(() => {
        setDiscountFailureMessage(InitialStateOfResponseMessage);
      }, 5000);
      return false;
    }
    
    // Validate Deal Registration Booster usage
    const dealRegBoosterDiscount = discountList.find(
      (d) => d.authorizedDiscountReason === "Deal Registration Booster"
    );
    if (dealRegBoosterDiscount) {
      const dealRegistrationDiscount = discountData.find(
        (item) => item.contractSelected === "Deal Registration"
      );
      if (!dealRegistrationDiscount) {
        setDiscountFailureMessage({
          showMessage: true,
          message: "Deal Registration Booster can only be used when Deal Registration discount is available.",
        });
        setTimeout(() => {
          setDiscountFailureMessage(InitialStateOfResponseMessage);
        }, 5000);
        return false;
      }
      
      // Check if the expiration date matches Deal Registration
      if (dealRegBoosterDiscount.expirationDate !== dealRegistrationDiscount.expirationDate) {
        setDiscountFailureMessage({
          showMessage: true,
          message: "Deal Registration Booster expiration date must match Deal Registration expiration date.",
        });
        setTimeout(() => {
          setDiscountFailureMessage(InitialStateOfResponseMessage);
        }, 5000);
        return false;
      }
    }
    const findIndex = discountList.findIndex(
      (d) =>
        d.authorizedDiscountPercentage === 0 ||
        d.authorizedDiscountPercentage === "" ||
        d.authorizedDiscountDateError ||
        d.authorizedDiscountError ||
        d.authorizedDiscountDecimalError ||
        d.minimumAuthorizedDiscountError
    );
    if (findIndex > -1) {
      if (
        discountList[findIndex].authorizedDiscountPercentage === 0 ||
        discountList[findIndex].authorizedDiscountPercentage === ""
      ) {
        discountList.splice(findIndex, 1, {
          ...discountList[findIndex],
          authorizedDiscountError: true,
        });
      }
      setDiscountFailureMessage({
        showMessage: true,
        message:
          "Unable to apply mentioned discounts due to some validation issue.",
      });
      setTimeout(() => {
        setDiscountFailureMessage(InitialStateOfResponseMessage);
      }, 5000);
      return false;
    }
    return true;
  };
  const onClickApplyDiscount = () => {
    if (validateDiscounts()) {
      const selectedDiscounts: string[] = [];
      const selectedDiscountDetails = discountList.map((d) => {
        selectedDiscounts.push(d.authorizedDiscountReason);
        return {
          expirationDate: d.expirationDate,
          authorizedDiscountReason: d.authorizedDiscountReason,
          authorizedDiscountAmount:
            (productNetAmount * Number(d.authorizedDiscountPercentage)) / 100,
          authorizedDiscountPercentage: Number(d.authorizedDiscountPercentage),
          minimumAuthorizedDiscountAmount: d.minimumAuthorizedDiscountAmount,
        };
      });
      if (selectedPivot === DeviceType.Standalone) {
        const authDiscountAmount =
          (productNetAmount * Number(totalPercentage)) / 100;
        quotationInfo.standaloneItems.splice(selectedProductIndex, 1, {
          ...selectedProduct,
          authorizedDiscount: authDiscountAmount,
          netAmount: parseFloat(
            (productNetAmount - authDiscountAmount).toFixed(2)
          ),
          discountedPercentage: totalPercentage,
          unitPrice: parseFloat(
            (
              (productNetAmount - authDiscountAmount) /
              selectedProduct.quantity
            ).toFixed(2)
          ),
          discountReason: selectedDiscounts.join(", "),
          authDiscountType: DiscountTypes[0].key,
          authDiscountAmounts: selectedDiscountDetails,
        });
        setQuotationInfo({
          ...quotationInfo,
          standaloneItems: [...quotationInfo.standaloneItems],
        });
      } else if (selectedPivot === DeviceType.Bundle) {
        const authDiscountAmount = productNetAmount * (totalPercentage / 100);
        quotationInfo.bundleItems.splice(selectedProductIndex, 1, {
          ...selectedProduct,
          authorizedDiscount: authDiscountAmount,
          netAmount: parseFloat(
            (productNetAmount - authDiscountAmount).toFixed(2)
          ),
          discountedPercentage: totalPercentage,
          unitPrice: parseFloat(
            (
              (productNetAmount - authDiscountAmount) /
              selectedProduct.quantity
            ).toFixed(2)
          ),
          discountReason: selectedDiscounts.join(", "),
          authDiscountType: DiscountTypes[0].key,
          authDiscountAmounts: selectedDiscountDetails,
        });
        setQuotationInfo({
          ...quotationInfo,
          bundleItems: [...quotationInfo.bundleItems],
        });
      }
      selection.setAllSelected(false);
      setLoading(true);
      dismissDiscountPanel();
    }
  };
  return (
    <form>
      <Stack verticalFill verticalAlign="start">
        <Stack>{renderCommandBar()}</Stack>
        <Stack>
          {onErrorCapture(
            discountSuccessMessage,
            setDiscountSuccessMessage,
            discountFailureMessage,
            setDiscountFailureMessage,
            discountInfoMessage,
            setDiscountInfoMessage
          )}
        </Stack>
        <Stack>{renderDiscountList()}</Stack>
        <br />
        {totalPercentage > 0 && (
          <p>
            <strong>Total Authorized Discount Percentage: </strong>
            {totalPercentage}%
          </p>
        )}
      </Stack>
    </form>
  );
});
